import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Eye, Type, Zap, Contrast, Check, AlertCircle } from 'lucide-react';
import {
  fetchAccessibilitySettings,
  updateAccessibilitySettings,
} from '../../../redux/settingsPages/accessibility/accessibilityActions';
import { clearAccessibilityError } from '../../../redux/settingsPages/accessibility/accessibilitySlice';

const TEXT_SCALE_OPTIONS = [
  { value: 'normal', labelKey: 'normal', sample: 'Aa' },
  { value: 'large', labelKey: 'large', sample: 'Aa' },
  { value: 'larger', labelKey: 'larger', sample: 'Aa' },
];

const ToggleRow = ({ icon: Icon, title, description, checked, onChange, disabled }) => (
  <div className="flex items-start justify-between gap-6 py-5">
    <div className="flex gap-4">
      <div className="mt-0.5 text-violet-500 dark:text-violet-300">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="font-medium text-gray-900 dark:text-white">{title}</div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 max-w-md">{description}</p>
      </div>
    </div>
    <button
      role="switch"
      aria-checked={checked}
      aria-label={title}
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        checked ? 'bg-gradient-to-r from-violet-500 to-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

const AccessibilitySettingsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    text_scale: textScale,
    reduced_motion: reducedMotion,
    high_contrast: highContrast,
    loading,
    updating,
    error,
    updateError,
    loaded,
  } = useSelector((state) => state.accessibility);

  const [justSaved, setJustSaved] = useState(false);

  // App.js already fetches this once, app-wide, whenever the user is
  // authenticated -- guarded on `loaded` so revisiting this settings page
  // doesn't redundantly re-fetch what App.js's mount effect already loaded.
  useEffect(() => {
    if (!loaded) dispatch(fetchAccessibilitySettings());
  }, [dispatch, loaded]);

  const applyChange = async (patch) => {
    try {
      await dispatch(updateAccessibilitySettings(patch)).unwrap();
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (err) {
      console.error('Failed to update accessibility settings:', err);
    }
  };

  const displayError = error || updateError;

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('settings.accessibility.pageTitle')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('settings.accessibility.pageSubtitle')}
        </p>
      </div>

      <div className="max-w-3xl space-y-6">
        {justSaved && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-600/30 rounded-lg p-4 flex items-center">
            <Check className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
            <p className="text-green-700 dark:text-green-400">{t('settings.accessibility.savedMessage')}</p>
          </div>
        )}

        {displayError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-600/30 rounded-lg p-4 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-red-700 dark:text-red-400">{displayError}</p>
            </div>
            <button
              onClick={() => dispatch(clearAccessibilityError())}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
              aria-label={t('settings.accessibility.dismissError')}
            >
              ✕
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
          </div>
        ) : (
          <>
            {/* Text size */}
            <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-1">
                <Type className="w-5 h-5 text-violet-500 dark:text-violet-300" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.accessibility.textSize.title')}</h2>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                {t('settings.accessibility.textSize.subtitle')}
              </p>
              <div className="grid grid-cols-3 gap-3">
                {TEXT_SCALE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => applyChange({ text_scale: opt.value })}
                    disabled={updating}
                    aria-pressed={textScale === opt.value}
                    className={`rounded-xl border p-4 text-center transition-colors disabled:opacity-50 ${
                      textScale === opt.value
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-500/10 dark:border-violet-400'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}
                  >
                    <div
                      className={`font-bold text-gray-900 dark:text-white ${
                        opt.value === 'large' ? 'text-xl' : opt.value === 'larger' ? 'text-2xl' : 'text-base'
                      }`}
                    >
                      {opt.sample}
                    </div>
                    <div
                      className={`text-xs mt-2 ${
                        textScale === opt.value
                          ? 'text-violet-700 dark:text-violet-300'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {t(`settings.accessibility.textSize.options.${opt.labelKey}`)}
                    </div>
                  </button>
                ))}
              </div>
            </section>

            {/* Motion & contrast */}
            <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-6 divide-y divide-gray-100 dark:divide-gray-700">
              <ToggleRow
                icon={Zap}
                title={t('settings.accessibility.reduceMotion.title')}
                description={t('settings.accessibility.reduceMotion.description')}
                checked={!!reducedMotion}
                disabled={updating}
                onChange={(val) => applyChange({ reduced_motion: val })}
              />
              <ToggleRow
                icon={Contrast}
                title={t('settings.accessibility.highContrast.title')}
                description={t('settings.accessibility.highContrast.description')}
                checked={!!highContrast}
                disabled={updating}
                onChange={(val) => applyChange({ high_contrast: val })}
              />
            </section>

            <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 flex items-start gap-3">
              <Eye className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('settings.accessibility.footerNote')}
              </p>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AccessibilitySettingsPage;
